import type { TabKey } from "@/components/GuidedRow";
import {
  GENRES,
  INSTRUMENTS,
  KEY_SIGNATURES,
  LANGUAGES,
  STYLES,
  TEMPOS,
  VOCAL_MODES,
  VOCAL_STYLES,
} from "@/constants";
import { sanitizeLyricLines } from "./lyricsSanitize";
import type {
  MusicEditorCustomData,
  MusicEditorCustomDataKey,
  MusicStemCustomDataKey,
} from "@/types/MusicEditorCustomData";
import { MUSIC_EDITOR_CUSTOM_DATA_KEYS } from "@/types/MusicEditorCustomData";

const STEM_KEYS = new Set<MusicStemCustomDataKey>([
  "vocals",
  "drums",
  "bass",
  "other",
]);

function isCustomDataKey(key: string): key is MusicEditorCustomDataKey {
  return (MUSIC_EDITOR_CUSTOM_DATA_KEYS as readonly string[]).includes(key);
}

function normalizeCustomDataField(value: unknown): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (Array.isArray(value)) {
    const joined = value
      .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
      .filter(Boolean)
      .join(", ");
    return joined || undefined;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return undefined;
}

function assignCustomDataField(
  target: MusicEditorCustomData,
  key: MusicEditorCustomDataKey,
  value: unknown,
): void {
  const normalized = normalizeCustomDataField(value);
  if (normalized) {
    target[key] = normalized;
  }
}

function parseCustomDataRecord(
  raw: unknown,
): Partial<MusicEditorCustomData> {
  if (!raw || typeof raw !== "object") {
    return {};
  }

  const parsed: Partial<MusicEditorCustomData> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!isCustomDataKey(key)) {
      continue;
    }
    assignCustomDataField(parsed, key, value);
  }
  return parsed;
}

/** Builds customData from guided chip selections (editor UI state). */
export function buildCustomDataFromGuidedSelections(
  guidedSelections?: Partial<Record<TabKey, string[]>> & {
    experience?: string[];
  },
): MusicEditorCustomData {
  const customData: MusicEditorCustomData = {};

  if (!guidedSelections) {
    return customData;
  }

  for (const key of MUSIC_EDITOR_CUSTOM_DATA_KEYS) {
    if (
      key === "prompt" ||
      key === "lyrics" ||
      key === "experience" ||
      STEM_KEYS.has(key as MusicStemCustomDataKey)
    ) {
      continue;
    }
    assignCustomDataField(customData, key, guidedSelections[key as TabKey]);
  }

  assignCustomDataField(customData, "experience", guidedSelections.experience);
  return customData;
}

/** Reads customData from doc profile customProps (same pattern as compositionPlan). */
export function parseCustomDataFromDocProfileCustomProps(
  customProps: Record<string, unknown> | null | undefined,
): Partial<MusicEditorCustomData> {
  if (!customProps) {
    return {};
  }

  const raw =
    customProps.customData ??
    customProps.CustomData ??
    customProps.custom_data;

  if (typeof raw === "string" && raw.trim()) {
    try {
      return parseCustomDataRecord(JSON.parse(raw) as unknown);
    } catch {
      return {};
    }
  }

  return parseCustomDataRecord(raw);
}

const METADATA_KEY_ALIASES: Record<string, MusicEditorCustomDataKey> = {
  prompt: "prompt",
  Prompt: "prompt",
  lyrics: "lyrics",
  Lyrics: "lyrics",
  genre: "genre",
  Genre: "genre",
  style: "style",
  Style: "style",
  tempo: "tempo",
  Tempo: "tempo",
  instruments: "instruments",
  Instruments: "instruments",
  bpm: "bpm",
  Bpm: "bpm",
  BPM: "bpm",
  key: "key",
  Key: "key",
  vocalmode: "vocalMode",
  vocalMode: "vocalMode",
  VocalMode: "vocalMode",
  vocalstyle: "vocalStyle",
  vocalStyle: "vocalStyle",
  VocalStyle: "vocalStyle",
  language: "language",
  Language: "language",
  experience: "experience",
  Experience: "experience",
};

function parseMetadataRecord(
  raw: Record<string, unknown>,
): Partial<MusicEditorCustomData> {
  const parsed: Partial<MusicEditorCustomData> = {};

  for (const [key, value] of Object.entries(raw)) {
    const mappedKey = METADATA_KEY_ALIASES[key];
    if (mappedKey) {
      assignCustomDataField(parsed, mappedKey, value);
    }
  }

  return parsed;
}

export interface StoredLyricsSection {
  section_name?: string;
  lines?: string[];
  duration_ms?: number;
}

/** Serializes lyrics for customData/queryData (separate from prompt). */
export function serializeLyricsSectionsForCustomData(
  sections: StoredLyricsSection[] | null | undefined,
): string | undefined {
  if (!sections?.length) {
    return undefined;
  }

  const normalized = sections
    .map((section) => ({
      ...(section.section_name && { section_name: section.section_name }),
      lines: sanitizeLyricLines(section.lines ?? []),
      ...(section.duration_ms !== undefined && { duration_ms: section.duration_ms }),
    }))
    .filter((section) => section.lines.length > 0);

  if (!normalized.length) {
    return undefined;
  }

  return JSON.stringify(normalized);
}

/** Parses customData `lyrics` into lyric sections for the lyrics panel. */
export function parseLyricsSectionsFromCustomData(
  lyricsValue: string | undefined,
): StoredLyricsSection[] | null {
  const raw = lyricsValue?.trim();
  if (!raw) {
    return null;
  }

  if (raw.startsWith("[")) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) {
        return null;
      }
      const sections: StoredLyricsSection[] = [];
      for (const entry of parsed) {
        if (!entry || typeof entry !== "object") {
          continue;
        }
        const section = entry as Record<string, unknown>;
        const lines = Array.isArray(section.lines)
          ? sanitizeLyricLines(
              section.lines.filter((line): line is string => typeof line === "string"),
            )
          : [];
        if (!lines.length) {
          continue;
        }
        sections.push({
          ...(typeof section.section_name === "string" && {
            section_name: section.section_name,
          }),
          lines,
          ...(typeof section.duration_ms === "number" && {
            duration_ms: section.duration_ms,
          }),
        });
      }
      return sections.length ? sections : null;
    } catch {
      return null;
    }
  }

  const lines = sanitizeLyricLines(raw.split(/\r?\n/));
  return lines.length ? [{ lines }] : null;
}

/** Builds customData persisted to queryData / doc profile (prompt vs lyrics are separate). */
export function buildPersistedEditorCustomData(options: {
  prompt?: string;
  guidedSelections?: Partial<Record<TabKey, string[]>> & { experience?: string[] };
  lyricsSections?: StoredLyricsSection[] | null;
}): MusicEditorCustomData {
  const customData = mergeMusicEditorCustomData(
    buildCustomDataFromGuidedSelections(options.guidedSelections),
  );

  const prompt = options.prompt?.trim();
  if (prompt) {
    customData.prompt = prompt;
  }

  const lyrics = serializeLyricsSectionsForCustomData(options.lyricsSections);
  if (lyrics) {
    customData.lyrics = lyrics;
  }

  return customData;
}

function hasGuidedCustomDataKeys(
  customData: Partial<MusicEditorCustomData>,
): boolean {
  return MUSIC_EDITOR_CUSTOM_DATA_KEYS.some(
    (key) => key !== "prompt" && !STEM_KEYS.has(key as MusicStemCustomDataKey) && !!customData[key],
  );
}

function addSelectionValue(
  selections: Record<string, string[]>,
  tab: string,
  value: string,
): void {
  const existing = selections[tab] ?? [];
  if (existing.includes(value)) {
    return;
  }
  selections[tab] = [...existing, value];
}

function mapChipTokenToSelections(
  token: string,
  selections: Record<string, string[]>,
): boolean {
  const trimmed = token.trim();
  if (!trimmed) {
    return false;
  }

  const bpmMatch = trimmed.match(/^(\d{2,3})\s*BPM$/i);
  if (bpmMatch) {
    addSelectionValue(selections, "bpm", bpmMatch[1]);
    return true;
  }

  if ((GENRES as readonly string[]).includes(trimmed)) {
    addSelectionValue(selections, "genre", trimmed);
    return true;
  }
  if ((STYLES as readonly string[]).includes(trimmed)) {
    addSelectionValue(selections, "style", trimmed);
    return true;
  }
  if ((TEMPOS as readonly string[]).includes(trimmed)) {
    addSelectionValue(selections, "tempo", trimmed);
    return true;
  }
  if ((INSTRUMENTS as readonly string[]).includes(trimmed)) {
    addSelectionValue(selections, "instruments", trimmed);
    return true;
  }
  if ((KEY_SIGNATURES as readonly string[]).includes(trimmed)) {
    addSelectionValue(selections, "key", trimmed);
    return true;
  }
  if ((VOCAL_MODES as readonly string[]).includes(trimmed)) {
    addSelectionValue(selections, "vocalMode", trimmed);
    return true;
  }
  if ((VOCAL_STYLES as readonly string[]).includes(trimmed)) {
    addSelectionValue(selections, "vocalStyle", trimmed);
    return true;
  }
  if ((LANGUAGES as readonly string[]).includes(trimmed)) {
    addSelectionValue(selections, "language", trimmed);
    return true;
  }

  return false;
}

function parseChipTokens(chipSection: string): Record<string, string[]> {
  const selections: Record<string, string[]> = {};
  for (const token of chipSection.split(",")) {
    mapChipTokenToSelections(token, selections);
  }
  return selections;
}

function selectionsToCustomData(
  selections: Record<string, string[]>,
): Partial<MusicEditorCustomData> {
  const customData: Partial<MusicEditorCustomData> = {};
  for (const [tab, values] of Object.entries(selections)) {
    if (!values.length) {
      continue;
    }
    if (isCustomDataKey(tab)) {
      assignCustomDataField(customData, tab, values);
    }
  }
  return customData;
}

function splitPromptAndEmbeddedSelections(prompt: string): {
  prompt: string;
  embedded: Partial<MusicEditorCustomData>;
} {
  const delimiterMatch = prompt.match(/\.\s*,\s*/);
  if (!delimiterMatch || delimiterMatch.index === undefined) {
    return { prompt, embedded: {} };
  }

  const chipSection = prompt.slice(
    delimiterMatch.index + delimiterMatch[0].length,
  );
  return {
    prompt,
    embedded: selectionsToCustomData(parseChipTokens(chipSection)),
  };
}

const PROMPT_CHIP_APPEND_ORDER: readonly (TabKey | "experience")[] = [
  "genre",
  "style",
  "tempo",
  "instruments",
  "key",
  "vocalMode",
  "vocalStyle",
  "language",
  "experience",
];

function collectChipValuesForPrompt(
  selections: Record<string, string[]>,
  bpm?: number,
): string[] {
  const values: string[] = [];

  for (const key of PROMPT_CHIP_APPEND_ORDER) {
    values.push(...(selections[key] ?? []));
  }

  const bpmValue = selections.bpm?.[0] ?? (bpm !== undefined ? String(bpm) : undefined);
  if (bpmValue) {
    values.push(`${bpmValue} BPM`);
  }

  return values;
}

/** Builds prompt textarea value: base prompt plus comma-appended chip labels. */
export function buildPromptAreaDisplayText(
  customData: Partial<MusicEditorCustomData>,
  selections: Record<string, string[]>,
  bpm?: number,
): string | undefined {
  const basePrompt = customData.prompt?.trim();
  if (!basePrompt) {
    return undefined;
  }

  const chipValues = collectChipValuesForPrompt(selections, bpm);
  if (!chipValues.length) {
    return basePrompt;
  }

  const missingChips = chipValues.filter((chip) => !basePrompt.includes(chip));
  if (!missingChips.length) {
    return basePrompt;
  }

  return `${basePrompt.replace(/,\s*$/, "")}, ${missingChips.join(", ")}`;
}

/** Reads editor metadata from doc profile `customProps.metadata` JSON. */
export function parseDocProfileMetadataCustomProps(
  customProps: Record<string, unknown> | null | undefined,
): Partial<MusicEditorCustomData> {
  if (!customProps) {
    return {};
  }

  const raw = customProps.metadata ?? customProps.Metadata;
  if (!raw) {
    return {};
  }

  let record: Record<string, unknown>;
  if (typeof raw === "string" && raw.trim()) {
    try {
      record = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  } else if (typeof raw === "object") {
    record = raw as Record<string, unknown>;
  } else {
    return {};
  }

  const structured = parseMetadataRecord(record);
  if (
    structured.prompt &&
    !structured.lyrics &&
    !hasGuidedCustomDataKeys(structured)
  ) {
    const { embedded } = splitPromptAndEmbeddedSelections(structured.prompt);
    return mergeMusicEditorCustomData(structured, embedded);
  }

  return structured;
}

export interface MusicEditorRestoreState {
  prompt?: string;
  selections?: Record<string, string[]>;
  bpm?: number;
}

const SELECTION_TAB_KEYS: readonly (TabKey | "experience")[] = [
  "genre",
  "style",
  "tempo",
  "instruments",
  "bpm",
  "key",
  "vocalMode",
  "vocalStyle",
  "language",
  "experience",
];

/** Maps stored customData strings back to guided chip selections. */
export function customDataToEditorSelections(
  customData: Partial<MusicEditorCustomData>,
): Record<string, string[]> {
  const selections: Record<string, string[]> = {};

  for (const key of SELECTION_TAB_KEYS) {
    const value = customData[key as keyof MusicEditorCustomData];
    if (!value) {
      continue;
    }
    const values = value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (values.length) {
      selections[key] = values;
    }
  }

  return selections;
}

export function buildEditorRestoreFromCustomData(
  customData: Partial<MusicEditorCustomData> | undefined,
): MusicEditorRestoreState | undefined {
  if (!customData) {
    return undefined;
  }

  const selections = customDataToEditorSelections(customData);
  const bpmToken = selections.bpm?.[0];
  const parsedBpm = bpmToken ? Number.parseInt(bpmToken.replace(/\D/g, ""), 10) : NaN;
  const bpm = Number.isFinite(parsedBpm) ? parsedBpm : undefined;
  const prompt = buildPromptAreaDisplayText(customData, selections, bpm);

  if (!prompt && Object.keys(selections).length === 0) {
    return undefined;
  }

  return {
    ...(prompt && { prompt }),
    ...(Object.keys(selections).length > 0 && { selections }),
    ...(bpm !== undefined && { bpm }),
  };
}

/** Reads customData from composition-plan metadata JSON (no extra API). */
export function parseCustomDataFromMetadataBlob(
  data: unknown,
): Partial<MusicEditorCustomData> {
  if (!data || typeof data !== "object") {
    return {};
  }

  const metadata = data as Record<string, unknown>;
  const fromRoot = parseCustomDataRecord(
    metadata.customData ?? metadata.custom_data ?? metadata.CustomData,
  );

  const compositionPlan = metadata.composition_plan;
  if (!compositionPlan || typeof compositionPlan !== "object") {
    return fromRoot;
  }

  const plan = compositionPlan as Record<string, unknown>;
  const fromPlan: Partial<MusicEditorCustomData> = { ...fromRoot };

  assignCustomDataField(
    fromPlan,
    "style",
    plan.positive_global_styles ?? plan.PositiveGlobalStyles,
  );

  const sections = plan.sections ?? plan.Sections;
  if (Array.isArray(sections)) {
    const localStyles = sections.flatMap((section) => {
      if (!section || typeof section !== "object") {
        return [];
      }
      const value = section as Record<string, unknown>;
      const styles = value.positive_local_styles ?? value.PositiveLocalStyles;
      return Array.isArray(styles)
        ? styles.filter((entry): entry is string => typeof entry === "string")
        : [];
    });
    if (localStyles.length) {
      assignCustomDataField(fromPlan, "instruments", localStyles);
    }
  }

  return fromPlan;
}

export function mergeMusicEditorCustomData(
  ...sources: Array<Partial<MusicEditorCustomData> | undefined>
): MusicEditorCustomData {
  const merged: MusicEditorCustomData = {};
  for (const source of sources) {
    if (!source) {
      continue;
    }
    for (const key of MUSIC_EDITOR_CUSTOM_DATA_KEYS) {
      const value = source[key];
      if (value) {
        merged[key] = value;
      }
    }
  }
  return merged;
}

/** Keeps separated prompt/lyrics when merging job metadata customData after generation. */
export function mergePostGenerationCustomData(
  existing: Partial<MusicEditorCustomData> | undefined,
  fromMetadata: Partial<MusicEditorCustomData> | undefined,
): MusicEditorCustomData {
  const overlay = { ...fromMetadata };
  if (existing?.lyrics?.trim()) {
    delete overlay.prompt;
  }
  return mergeMusicEditorCustomData(existing, overlay);
}

export function applyStemSelectionToCustomData(
  customData: MusicEditorCustomData,
  stemKey?: string,
): MusicEditorCustomData {
  if (!stemKey || !STEM_KEYS.has(stemKey as MusicStemCustomDataKey)) {
    return customData;
  }
  return {
    ...customData,
    [stemKey]: "true",
  };
}

export function hasMusicEditorCustomData(
  customData: Partial<MusicEditorCustomData> | undefined,
): boolean {
  if (!customData) {
    return false;
  }
  return MUSIC_EDITOR_CUSTOM_DATA_KEYS.some((key) => !!customData[key]);
}

/** Serializes only defined interface keys for queryData / customProps storage. */
export function serializeMusicEditorCustomData(
  customData: Partial<MusicEditorCustomData> | undefined,
): Record<string, string> | undefined {
  if (!hasMusicEditorCustomData(customData)) {
    return undefined;
  }

  const serialized: Record<string, string> = {};
  for (const key of MUSIC_EDITOR_CUSTOM_DATA_KEYS) {
    const value = customData?.[key];
    if (value) {
      serialized[key] = value;
    }
  }
  return Object.keys(serialized).length > 0 ? serialized : undefined;
}

/** Doc profile API expects customProps.customData as a JSON string, not an object. */
export function serializeCustomDataForDocProfile(
  customData: Partial<MusicEditorCustomData> | undefined,
): string | undefined {
  const serialized = serializeMusicEditorCustomData(customData);
  return serialized ? JSON.stringify(serialized) : undefined;
}

/** Ensures customProps.customData is a string before doc profile create/update. */
export function normalizeDocProfileCustomPropsForApi(
  customProps: Record<string, unknown> | null | undefined,
): Record<string, unknown> | undefined {
  if (!customProps) {
    return undefined;
  }

  const normalized = { ...customProps };
  const raw =
    normalized.customData ?? normalized.CustomData ?? normalized.custom_data;
  if (raw && typeof raw === "object") {
    const json = JSON.stringify(raw);
    normalized.customData = json;
    normalized.CustomData = json;
    normalized.custom_data = json;
  }
  return normalized;
}

export function normalizeDocProfileForApi(
  profile: Record<string, unknown>,
): Record<string, unknown> {
  const normalized = { ...profile };
  const customProps =
    (normalized.customProps as Record<string, unknown> | undefined) ??
    (normalized.CustomProps as Record<string, unknown> | undefined);
  const fixedProps = normalizeDocProfileCustomPropsForApi(customProps);
  if (fixedProps) {
    normalized.customProps = fixedProps;
    normalized.CustomProps = fixedProps;
  }
  return normalized;
}
